import { useState } from 'react';
import { AxiosResponse } from 'axios';

import { ApiClientEnterprise, ApiClientMain } from '@helpers/apiClients';
import { useMutation } from 'react-query';

interface UsePresignedUrlReturn {
  presignedUrl: string | null;
  imageUrl: string | null;
  error: Error | null;
  loading: boolean;
  refreshUrl: () => void;
  getPresignedUrl: () => Promise<{ presignedUrl: string; imageUrl: string }>;
}

interface IPresignedUrlResponse {
  result: {
    url: string;
    key: string;
    bucket: string;
  };
}

export const usePresignedUrl = (url: string, isMidnight = false): UsePresignedUrlReturn => {
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const { mutate: fetchPresignedUrl, isLoading: loading } = useMutation(
    async () => {
      const response: AxiosResponse<IPresignedUrlResponse> = await (isMidnight
        ? ApiClientEnterprise
        : ApiClientMain
      ).post(url);

      return response.data.result;
    },
    {
      onSuccess: (data) => {
        setPresignedUrl(data.url);
        setImageUrl(data.key);
        setError(null);
      },
      onError: (err) => {
        setError(err instanceof Error ? err : new Error('Failed to fetch presigned URL'));
        setPresignedUrl(null);
      },
    },
  );

  const getPresignedUrl = (): Promise<{ presignedUrl: string; imageUrl: string }> => {
    return new Promise((resolve, reject) => {
      fetchPresignedUrl(undefined, {
        onSuccess: (data) => {
          resolve({
            presignedUrl: data.url,
            imageUrl: data.key,
          });
        },
        onError: (error) => {
          reject(error);
        },
      });
    });
  };

  return {
    presignedUrl,
    imageUrl,
    error,
    loading,
    refreshUrl: fetchPresignedUrl,
    getPresignedUrl,
  };
};
